






























































import './GuestRegisterForm.scss'
import { defineComponent, ref, computed } from '@vue/composition-api'
import { useValidationCheck, useCountries } from '@/modules/kisweui/compositions'
import { ValidationRules } from '@/modules/kisweui/types/validation'
import { required } from '@vuelidate/validators'
import { AuthenticatorForm } from '@/modules/user/components'
import { KisweFormField, KisweFormGroup, KisweInput, KisweSwitch } from '@/modules/kisweui/components'
import { useCastWelcome } from '@/modules/casts/compositions'
import useTranslations from '@/compositions/translations'
import { UserProfile } from '@/types/users'
import { TranslateResult } from 'vue-i18n'
import { OLYMPIC_TOS_URL } from '@/modules/kisweui/data'
import store from '@/store'
import thorapi from '@/modules/thorapi'
import { getFanRoomLogData } from '@/modules/olympics/utils'
import { CastType } from '@/types/casts'
import { Cast } from '@/modules/casts/classes/cast'

const rules: ValidationRules = {
  firstName: { required },
  lastName: { required },
  email: { required }
}

export default defineComponent({
  name: 'GuestRegisterForm',
  components: {
    AuthenticatorForm,
    KisweFormField,
    KisweFormGroup,
    KisweInput,
    KisweSwitch
  },
  props: {
    buttonLabel: {
      type: String as () => string|TranslateResult,
      required: false,
      default: ''
    },
    cast: {
      type: Object as () => Cast,
      required: true
    },
    profile: {
      type: Object as () => UserProfile,
      required: true
    }
  },
  // eslint-disable-next-line max-lines-per-function
  setup (props) {
    const { getTranslation } = useTranslations()
    const { allCountries } = useCountries()
    const { requestAddParticipant } = useCastWelcome()
    const guestForm = ref({ firstName: '', lastName: '', email: '' })
    const consents = ref({ terms: false, age: false, privacy: false })
    const countryCode = ref('')
    const { validator, isValidForm, showError } = useValidationCheck(rules, guestForm)
    const ageLimit = computed(() => allCountries.value[countryCode.value]?.ageLimit.toString() ?? '13')
    const tosUrl = computed(() => OLYMPIC_TOS_URL[props.profile.locale])
    const showPrivacy = computed(() => allCountries.value[countryCode.value]?.privacy ?? false)
    const validatorError = computed(() => (validator.value.$errors.length === 0)
      ? '' : getTranslation('general_requiredallfields_error'))
    const isDisabled = computed(() => countryCode.value === '' || validatorError.value !== '' || !consents.value.terms
      || !consents.value.age || showPrivacy.value && !consents.value.privacy)
    // eslint-disable-next-line max-lines-per-function
    const submit = async () => {
      if (!isValidForm() || !props.cast.id || !props.profile.id) return
      const { firstName, lastName, email } = guestForm.value
      const updatedProfile = new UserProfile({ ...props.profile, first_name: firstName, last_name: lastName, email })
      await store.dispatch.user.updateProfile(updatedProfile)
      await store.dispatch.user.setUserConsent({
        userId: updatedProfile.id,
        consentInfo: {
          consents: {
            terms: consents.value.terms,
            age: consents.value.age,
            privacy: showPrivacy.value ? consents.value.privacy : true
          },
          email, firstName, lastName, locale: updatedProfile.locale
        }
      })
      await requestAddParticipant({ castId: props.cast.id, userId: updatedProfile.id, name: updatedProfile.fullName })
      if (props.cast.castType === CastType.FanRoom) {
        const logMessageData = {
          guestCountry: countryCode.value,
          ...getFanRoomLogData(props.cast)
        }
        thorapi.analytics.anonymousGuestRegistered(props.cast.id, logMessageData)
      }
    }
    return {
      allCountries,
      ageLimit,
      consents,
      countryCode,
      getTranslation,
      isDisabled,
      tosUrl,
      showError,
      showPrivacy,
      submit,
      validator,
      validatorError
    }
  },
  async mounted () {
    const result = await thorapi.analytics.guestRegistrationPageLoad(this.cast.id, this.cast.watermark)
    if (result.isFailure) {
      this.countryCode = 'USA'
      return
    }
    const clientCountry = Object.values(this.allCountries).find((country) =>
      country.isoCode === result.value.clientCountryCode)
    this.countryCode = clientCountry?.code ?? 'USA'
  }
})
